import React, { useState } from 'react'
import {
  Heading,
  Text,
  Button,
  TextInput,
  FormControl,
  InlineLink
} from '@primer/react-brand'
import ChromelessAuthLayout from '../components/ChromelessAuthLayout'
import { validateRequired } from '../utilities/validateAuthForm'

// PASSWORDLESS SINCE 2026-09-13 (Cameron): an email, then a link. See
// docs/superpowers/specs/2026-09-13-civic-lens-passwordless-access-design.md.
//
// The confirmation reads the same whether or not the address has an account —
// the endpoint answers identically, and the page must not undo that.
const SignInPage = ({ signUpPath = '/sign-up', redirectTo, email }) => {
  const [emailValue, setEmailValue] = useState(email || '')
  const [sentTo, setSentTo] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const requestLink = async (address) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/v1/auth/sign_in_link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'same-origin',
        body: JSON.stringify({ email: address, redirect_to: redirectTo })
      })
      const data = await response.json()

      if (data.success) {
        setSentTo(address)
      } else if (response.status === 429) {
        setError({ type: 'general', message: 'Too many attempts. Please wait a minute and try again.' })
      } else {
        setError({ type: 'general', message: 'Something went wrong. Please try again.' })
      }
    } catch (err) {
      setError({ type: 'general', message: 'Something went wrong. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const address = emailValue.trim()
    const requiredError = validateRequired([{ value: address, message: 'Email is required.' }])
    if (requiredError) {
      setError(requiredError)
      return
    }
    requestLink(address)
  }

  if (sentTo) {
    return (
      <ChromelessAuthLayout>
        <Heading as="h1" size="5">Check your email</Heading>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
          If there's a CivicLens account for <strong style={{ color: 'var(--base-color-scale-gray-9)' }}>{sentTo}</strong>, we've sent it a sign-in link. The link works once and expires in 15 minutes.
        </Text>

        {error && (
          <p style={{ marginTop: '12px', color: 'var(--brand-color-error-fg)', fontSize: '14px' }}>
            {error.message}
          </p>
        )}

        <Text size="200" variant="muted" as="div" style={{ marginTop: '22px' }}>
          Didn't get it?{' '}
          <InlineLink onClick={() => requestLink(sentTo)} style={{ cursor: 'pointer' }}>
            {loading ? 'Sending...' : 'Send it again'}
          </InlineLink>
        </Text>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '6px' }}>
          <InlineLink onClick={() => { setSentTo(null); setError(null) }} style={{ cursor: 'pointer' }}>
            Use a different email
          </InlineLink>
        </Text>
      </ChromelessAuthLayout>
    )
  }

  return (
    <ChromelessAuthLayout>
      <Heading as="h1" size="5">Sign In</Heading>
      <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
        Welcome back to CivicLens
      </Text>

      <form onSubmit={handleSubmit} style={{ marginTop: '28px' }}>
        <FormControl fullWidth validationStatus={error ? 'error' : undefined}>
          <FormControl.Label>Email</FormControl.Label>
          <TextInput
            name="email"
            type="email"
            value={emailValue}
            onChange={(e) => { setEmailValue(e.target.value); if (error) setError(null) }}
            placeholder="Enter your email"
          />
          {error && (
            <FormControl.Validation validationStatus="error">{error.message}</FormControl.Validation>
          )}
        </FormControl>

        <Button
          type="submit"
          variant="primary"
          block={true}
          disabled={loading}
          style={{
            marginTop: '18px',
            backgroundColor: 'var(--base-color-scale-blue-5)',
            borderColor: 'var(--base-color-scale-blue-5)',
            borderRadius: '6px'
          }}
        >
          {loading ? 'Sending link...' : 'Email me a sign-in link'}
        </Button>

        <Text size="200" variant="muted" as="div" align="center" style={{ marginTop: '24px' }}>
          Don't have an account?{' '}
          <InlineLink href={signUpPath}>Get Started Free</InlineLink>
        </Text>
      </form>
    </ChromelessAuthLayout>
  )
}

export default SignInPage
